import { useMemo, useState } from "react";
import { Tooltip, Legend, ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Bar } from "recharts";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { fetchEvaluationThumbsThunk } from "../../../store/thunk/reportingThunk";
import { useEvaluationThumbsData } from "../../../store/selectors/reportingSelectors";
import { EvaluationStarsDataWrapper, FeedbackTableContainer, NoDataContainer, NoFeedbackContainer, NoFeedbackText } from "../Dashboard.styles";
import { Box, IconButton, Paper, TableContainer, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { chartColorList } from "../constants";
import { FeedbackData } from "../../../types/reportingTypes";
import { ActionRow, BorderRow, CellBox, NoDataText, OptionContainer, OptionRow, RowWrapper, TableWrapper } from "./Widget.styles";
import { TableColumnHeaderWrapper } from "../../../components/common/table/TableElements.styles";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { GeneralButton } from "../../../common/ui/GeneralButton";

type Props = {
  actionModuleId: number;
};

export const EvaluationThumbsChart: React.FC<Props> = ({ actionModuleId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [hasApplied, setHasApplied] = useState(false);
  let evaluationThumbsData = useEvaluationThumbsData() || [];

  const isDataValid = useMemo(() => {
    return (
      evaluationThumbsData.length > 0 &&
      evaluationThumbsData.every(
        (data) =>
          !isNaN(data.positiveAnswered) &&
          !isNaN(data.negativeAnswered) &&
          !isNaN(data.notAnswered)
      )
    );
  }, [evaluationThumbsData]);

  const handleStartDateChange = (newDate: dayjs.Dayjs | null) => {
    setStartDate(newDate?.format('YYYY-MM-DD') || null);
  };

  const handleEndDateChange = (newDate: dayjs.Dayjs | null) => {
    setEndDate(newDate?.format('YYYY-MM-DD') || null);
  };

  const fetchChartData = async () => {
    if (!startDate || !endDate) return;
    setHasApplied(true);
    try {
      await dispatch(fetchEvaluationThumbsThunk({ actionModuleId, startDate, endDate }));
    } catch (error) {
      console.error("Error fetching evaluation thumbs data:", error);
    }
  };

  const resetChartData = () => {
    setStartDate(null);
    setEndDate(null);
    setHasApplied(false);
  };

  const isDateRangeValid = startDate && endDate && dayjs(endDate).isAfter(dayjs(startDate));
  const feedbackList = evaluationThumbsData.flatMap((data) => data.feedback);

  return (
    <>
      <OptionContainer>
        <OptionRow>
          <DatePicker
            label={t("dashboardPage.startDate")}
            value={startDate ? dayjs(startDate) : null}
            onChange={handleStartDateChange}
            format="DD.MM.YYYY"
            slotProps={{ textField: { size: "small", sx: { width: '35%', margin: '0 5px' } } }}
          />
          <DatePicker
            label={t("dashboardPage.endDate")}
            value={endDate ? dayjs(endDate) : null}
            onChange={handleEndDateChange}
            format="DD.MM.YYYY"
            slotProps={{ textField: { size: "small", sx: { width: '35%', margin: '0 5px' } } }}
          />
          <IconButton
            onClick={resetChartData}
            aria-label={t("buttons.reset")}
            sx={{ marginRight: 1, color: "#555" }}
            size="small"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </OptionRow>
        <ActionRow>
          <GeneralButton
            appVariant="widget"
            onClick={fetchChartData}
            disabled={!isDateRangeValid}
          >
            {t("buttons.apply")}
          </GeneralButton>
        </ActionRow>
      </OptionContainer>

      {hasApplied && !isDataValid && (
        <NoDataContainer>
          <NoDataText>{t("dashboardPage.evaluationThumbs.noDisplayableData")}</NoDataText>
        </NoDataContainer>
      )}

      {hasApplied && isDataValid && (
        <>
          <EvaluationStarsDataWrapper sx={{
            p: 2,
            maxWidth: '100%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}>
            <Box sx={{ height: 300, width: '100%' }}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={evaluationThumbsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="endDate" tickFormatter={(v) => dayjs(v).format("DD.MM.YYYY")} />
                  <YAxis domain={[0, 'dataMax']} />
                  <Tooltip labelFormatter={(v) => dayjs(v).format("DD.MM.YYYY")} />
                  <Legend />
                  <Bar
                    dataKey="positiveAnswered"
                    name={t("dashboardPage.evaluationThumbs.positive")}
                    stackId="a"
                    fill={chartColorList[0]}
                  />
                  <Bar dataKey="negativeAnswered" name={t("dashboardPage.evaluationThumbs.negative")} stackId="a" fill={chartColorList[1]} />
                  <Bar dataKey="notAnswered" name={t("dashboardPage.evaluationThumbs.notAnswered")} stackId="a" fill={chartColorList[2]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ marginTop: 5, width: "100%" }}>
              <FeedbackTableContainer
                sx={{
                  maxHeight: 250,
                  overflowY: 'auto',
                  width: '100%',
                }}
              >
                {feedbackList.length > 0
                  ?
                  <TableContainer component={Paper} sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    <TextFeedbackTable feedback={feedbackList} />
                  </TableContainer>
                  :
                  <NoFeedbackContainer>
                    <NoFeedbackText variant="h6" sx={{ color: 'gray' }}>
                      {t("dashboardPage.evaluationThumbs.noFeedback")}
                    </NoFeedbackText>
                  </NoFeedbackContainer>
                }
              </FeedbackTableContainer>
            </Box>
          </EvaluationStarsDataWrapper>
        </>
      )}
    </>
  );
};

export const TextFeedbackTable: React.FC<{ feedback: FeedbackData[] }> = ({
  feedback,
}) => {
  const { t } = useTranslation();

  return (
    <TableWrapper>
      <TableColumnHeaderWrapper>{t("tableColumnHeaders.name")}</TableColumnHeaderWrapper>
      <TableColumnHeaderWrapper>{t("tableColumnHeaders.date")}</TableColumnHeaderWrapper>
      <TableColumnHeaderWrapper>{t("tableColumnHeaders.answer")}</TableColumnHeaderWrapper>
      <TableColumnHeaderWrapper>{t("tableColumnHeaders.feedback")}</TableColumnHeaderWrapper>

      <BorderRow />

      <Box sx={{ display: "contents" }}>
        {feedback.map((item, index) => (
          <RowWrapper key={index}>
            <CellBox>
              <Typography variant="body2" noWrap title={item.name}>
                {item.name}
              </Typography>
            </CellBox>
            <CellBox>
              <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word" }} title={dayjs(item.date).format("DD.MM.YYYY")}>
                {dayjs(item.date).format("DD.MM.YYYY")}
              </Typography>
            </CellBox>
            <CellBox>
              <Typography variant="body2" noWrap title={
                item.answer === "ThumbsUp"
                  ? t("dashboardPage.evaluationThumbs.positive")
                  : t("dashboardPage.evaluationThumbs.negative")
              }>
                {item.answer === "ThumbsUp"
                  ? t("dashboardPage.evaluationThumbs.positive")
                  : t("dashboardPage.evaluationThumbs.negative")}
              </Typography>
            </CellBox>
            <CellBox>
              <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                {item.text}
              </Typography>
            </CellBox>
            <BorderRow />
          </RowWrapper>
        ))}
      </Box>
    </TableWrapper>
  );
};